// src/components/ExpeditorsPage/ExpeditorsPage.jsx
import React, { useState, useEffect } from "react";
import Button from "../UI/Button";
import Card from "../UI/Card";
import Modal from "../UI/Modal";
import ConfirmModal from "../UI/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authFetch } from "../api/api";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/expeditors/`;




const ExpeditorsPage = () => {
  const [expeditors, setExpeditors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingExpeditor, setEditingExpeditor] = useState(null);
  const [formData, setFormData] = useState({ name: "", type: "both", comment: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expeditorToDelete, setExpeditorToDelete] = useState(null);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();
  
  const { t } = useTranslation("ExpeditorsPage");


  // --- Загрузка экспедиторов с API ---
  useEffect(() => {
    authFetch(API_URL)
      .then((res) => res.json())
      .then(setExpeditors)
      .catch((err) => console.error("Ошибка загрузки экспедиторов:", err));
  }, []);

  // --- Обработка открытия формы ---
  const handleOpenForm = (expeditor = null) => {
    if (expeditor) {
      setEditingExpeditor(expeditor);
      setFormData({ name: expeditor.name, type: expeditor.type, comment: expeditor.comment || "" });
    } else {
      setEditingExpeditor(null);
      setFormData({ name: "", type: "both", comment: "" });
    }
    setShowForm(true);
  };

  // --- Сохранение экспедитора ---
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const method = editingExpeditor ? "PUT" : "POST";
      const url = editingExpeditor ? `${API_URL}${editingExpeditor.id}/` : API_URL;

      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Ошибка при сохранении");

      const savedExpeditor = await res.json();
      setExpeditors((prev) =>
        editingExpeditor
          ? prev.map((ex) => (ex.id === savedExpeditor.id ? savedExpeditor : ex))
          : [...prev, savedExpeditor]
      );

      setShowForm(false);
      setEditingExpeditor(null);
      setMessage(t("message_saved"));
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      console.error(err);
      setMessage(t("message_error_save"));
      setTimeout(() => setMessage(null), 2500);
    }
  };

  // --- Удаление экспедитора ---
  const handleDelete = async () => {
    if (!expeditorToDelete) return;
    try {
      const res = await authFetch(`${API_URL}${expeditorToDelete.id}/`, { method: "DELETE" });
      if (!res.ok) throw new Error("Ошибка удаления");
      setExpeditors((prev) => prev.filter((ex) => ex.id !== expeditorToDelete.id));
      setShowDeleteConfirm(false);
      setExpeditorToDelete(null);
      setMessage(t("message_deleted"));
      setTimeout(() => setMessage(null), 2500);
    } catch (err) {
      console.error(err);
      setMessage(t("message_error_delete"));
      setTimeout(() => setMessage(null), 2500);
    }
  };

  return (
  <div className="w-full max-w-4xl mx-auto mt-10">

    {/* HEADER */}
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-2xl font-bold">{t("expeditors")}</h2>

      <Button
        variant="primary"
        onClick={() => handleOpenForm()}
      >
        {t("add_expeditor")}
      </Button>
    </div>

    {/* MESSAGE */}
    {message && (
      <div className="mb-4 p-3 bg-green-500 text-white rounded-xl">
        {message}
      </div>
    )}

    {/* LIST */}
    <div className="space-y-4">
      {expeditors.map((ex) => (
      <Card
        key={ex.id}
        hoverable
        onClick={() => navigate(`/expeditors/${ex.id}`)}
        className="flex justify-between items-start cursor-pointer"
      >

          {/* LEFT INFO */}
          <div className="space-y-1">
            <p className="font-semibold text-lg">
              {ex.name}
            </p>

            <p>
              <span className="font-medium">{t("type")}:</span>{" "}
              {ex.type === "cargo"
                ? t("type_cargo")
                : ex.type === "official"
                ? t("type_official")
                : t("type_both")}
            </p>

    
          </div>

          {/* ACTIONS */}
          <div className="flex gap-2">
            <div
              className="flex gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="edit"
                onClick={() => handleOpenForm(ex)}
              >
                {t("edit")}
              </Button>
            </div>
            
            <div
              className="flex gap-2"
              onClick={(e) => e.stopPropagation()}
            >

              <Button
                variant="danger"
                onClick={() => {
                  setExpeditorToDelete(ex);
                  setShowDeleteConfirm(true);
                }}
              >
                {t("delete")}
              </Button>
              </div>
          </div>

        </Card>
      ))}
    </div>

    {/* FORM MODAL */}
    <Modal
      isOpen={showForm}
      onClose={() => {
        setShowForm(false);
        setEditingExpeditor(null);
      }}
      title={
        editingExpeditor
          ? t("edit")
          : t("add")
      }
    >
      <form onSubmit={handleSave} className="space-y-4">

        <div>
          <label className="block mb-1 font-medium">
            {t("name")}
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            className="w-full rounded-xl border px-4 py-3"
            required
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">
            {t("type")}
          </label>
          <select
            value={formData.type}
            onChange={(e) =>
              setFormData({ ...formData, type: e.target.value })
            }
            className="w-full rounded-xl border px-4 py-3"
          >
            <option value="cargo">{t("type_cargo")}</option>
            <option value="official">{t("type_official")}</option>
            <option value="both">{t("type_both")}</option>
          </select>
        </div>

        <div>
          <label className="block mb-1 font-medium">
            {t("comment")}
          </label>
          <textarea
            rows={4}
            value={formData.comment}
            onChange={(e) =>
              setFormData({ ...formData, comment: e.target.value })
            }
            className="w-full rounded-xl border px-4 py-3"
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={() => setShowForm(false)}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="primary"
            type="submit"
          >
            {t("save")}
          </Button>
        </div>

      </form>
    </Modal>
    <ConfirmModal
  isOpen={showDeleteConfirm}
  onClose={() => {
    setShowDeleteConfirm(false);
    setExpeditorToDelete(null);
  }}
  onConfirm={handleDelete}
  title={t("confirm_delete_title")}
  description={
    <>
      {t("confirm_delete_text")}{" "}
      <span className="font-semibold">
        {expeditorToDelete?.name}
      </span>
      ?
    </>
  }
  confirmText={t("confirm_delete_button")}
/>
  </div>
);
};

export default ExpeditorsPage;